import React, { useMemo } from 'react';
import { history, useIntl, useLocation, useParams } from 'umi';
import { Button } from 'tea-component';
import { IStudioInfo, IDashboardItem } from '@/pages/home/pages/studio/type';
import StudioHeader from '@/pages/home/pages/studio/components/StudioHeader';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import OverviewIcon from '@home/assets/svg/studio/home/overview-icon.svg';
import OverviewIconWhite from '@home/assets/svg/studio/home/overview-icon-white.svg';
import CompetitorIcon from '@home/assets/svg/studio/home/competitor-icon.svg';
import CompetitorIconWhite from '@home/assets/svg/studio/home/competitor-icon-white.svg';
import '../../index.scss';

type TCasualHubTabKey = 'overview' | 'competitors';

interface ICasualHubChromeProps {
  studioInfo: IStudioInfo;
  enabledTabs?: TCasualHubTabKey[];
  hideTabSwitcher?: boolean;
}

interface ICasualHubParams {
  studioId?: string;
}

const DEFAULT_ENABLED_TABS: TCasualHubTabKey[] = ['overview', 'competitors'];

const PATH_TO_TAB_KEY: Record<string, string> = {
  '/home': 'overview',
  '/competitors': 'competitors',
};

const TAB_ICON_MAP: Record<string, { normal: string; active: string; }> = {
  overview: { normal: OverviewIcon, active: OverviewIconWhite },
  competitors: { normal: CompetitorIcon, active: CompetitorIconWhite },
};

/**
 * Casual 顶栏：标题 + Overview/竞品大按钮 + 紧随其后的游戏切换（仅游戏卡）
 */
const CasualHubChrome: React.FC<ICasualHubChromeProps> = ({
  studioInfo,
  enabledTabs = DEFAULT_ENABLED_TABS,
  hideTabSwitcher = false,
}) => {
  const { studioId } = useParams() as ICasualHubParams;
  const location = useLocation();
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const { name_en: titleEn, name_cn: titleCn } = studioInfo?.data?.studio_info || {};

  const dashboardList: IDashboardItem[] =
    (studioInfo?.data as any)?.studio_dashboard_list || [];
  const routeBasePath = studioId ? `/studio/${studioId}/home` : '';

  const tabs = useMemo(() => {
    return dashboardList
      .map((item: IDashboardItem) => {
        const key = PATH_TO_TAB_KEY[item.path] || item.path?.replace(/^\//, '');
        return {
          key,
          labelEn: item.name_en,
          labelCn: item.name_cn,
          routePath: routeBasePath
            ? `${routeBasePath}${key === 'overview' ? '' : `/${key}`}`
            : '',
        };
      })
      .filter((item) => enabledTabs.includes(item.key as TCasualHubTabKey));
  }, [dashboardList, routeBasePath, enabledTabs]);

  const defaultKey = tabs[0]?.key || enabledTabs[0] || 'overview';
  const knownTabKeys = useMemo(() => new Set(tabs.map(t => t.key)), [tabs]);

  const tabFromPath = useMemo(() => {
    if (!studioId) {
      return undefined;
    }
    const match = location.pathname.match(
      new RegExp(`^/studio/${studioId}/home(?:/([^/?#]+))?/?$`),
    );
    if (!match) {
      return undefined;
    }
    if (!match[1]) {
      return undefined;
    }
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return undefined;
    }
  }, [location.pathname, studioId]);

  const isCasualHomePath = Boolean(
    studioId &&
      new RegExp(`^/studio/${studioId}/home(?:/|$)`).test(location.pathname),
  );
  let activeKey = '';
  if (isCasualHomePath) {
    activeKey = tabFromPath && knownTabKeys.has(tabFromPath)
      ? tabFromPath
      : defaultKey;
  }

  const handleTabClick = (tabKey: string, routePath: string): void => {
    if (tabKey !== activeKey && routePath) {
      history.push(routePath);
    }
  };

  return (
    <>
      <div className="dbh-casual-hub-header-container">
        <div className="dbh-casual-hub-header">
          {getName(isEn, titleEn, titleCn)}
          <div className="dbh-casual-hub-header-desc">
            {getName(
              isEn,
              'Insights into IEGG\'s casual games and competitors',
              'Insights into IEGG\'s casual games and competitors',
            )}
          </div>
        </div>
      </div>
      <div className="dbh-casual-hub__toolbar">
        {!hideTabSwitcher && (
          <div className="dbh-casual-hub__tabs">
            {tabs.map((tabItem) => {
              const isActive = activeKey === tabItem.key;
              const label = isEn ? tabItem.labelEn : tabItem.labelCn;
              return (
                <Button
                  key={tabItem.key}
                  className={`dbh-casual-hub__tab ${isActive ? 'dbh-casual-hub__tab--active' : ''}`}
                  onClick={() => handleTabClick(tabItem.key, tabItem.routePath)}
                >
                  <span className="dbh-casual-hub__tab-icon">
                    <img
                      src={
                        isActive
                          ? TAB_ICON_MAP[tabItem.key]?.active
                          : TAB_ICON_MAP[tabItem.key]?.normal
                      }
                      className="dbh-casual-hub__tab-svg"
                      alt=""
                    />
                  </span>
                  <span className="dbh-casual-hub__tab-label">{label}</span>
                </Button>
              );
            })}
          </div>
        )}
        <div className="dbh-casual-hub__game-switch">
          <StudioHeader studioInfo={studioInfo} gamesOnly />
        </div>
      </div>
    </>
  );
};

export default CasualHubChrome;
